import React, { createContext, useEffect, useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
  PALETTES,
  DEFAULT_PALETTE_ID,
  DEFAULT_MODE,
  resolveTokens,
  resolveShell,
  applyTokens,
  applyMode,
  paletteStore,
  modeStore,
  nightModeAvailable,
} from '../utils/palette';

export const ThemeContext = createContext();

const ThemeContextProvider = (props) => {
  const [sideBarStyle, setSideBarStyle] = useState({ value: 'full', label: 'Full' });
  const [sidebarposition, setSidebarposition] = useState({ value: 'fixed', label: 'Fixed' });
  const [headerposition, setHeaderposition] = useState({ value: 'fixed', label: 'Fixed' });
  const [sidebarLayout, setSidebarLayout] = useState({ value: 'vertical', label: 'Vertical' });
  const [direction, setDirection] = useState({ value: 'ltr', label: 'LTR' });
  const [primaryColor, setPrimaryColor] = useState('color_1');
  const [navigationHader, setNavigationHader] = useState('color_3');
  const [haderColor, setHaderColor] = useState('color_1');
  const [sidebarColor, setSidebarColor] = useState('color_1');
  const [iconHover, setIconHover] = useState(false);
  const [sidebariconHover, setSidebariconHover] = useState(false);
  const [menuToggle, setMenuToggle] = useState(true);
  // data-theme-version stays 'light' at all times, night mode included:
  // the vendor stylesheets key ordinary styling off its presence, and the
  // dark variants of the retired dark and indigo skins must never match.
  // Day and night are a separate attribute -- data-mode on <html>, see
  // `mode` below and css/theme/modes.css.
  const [background] = useState({ value: 'light', label: 'Light' });

  // The chosen palette: { id } for a prebuilt one, or
  // { id: 'custom', accent, tint } for one the user mixed. Read
  // through the storage adapter, which also migrates anyone still
  // carrying a retired skin.
  const [palette, setPalette] = useState(() => paletteStore.read());

  // Day or night: a second axis beside the palette, not a palette of its
  // own. The stored choice is held at day until night mode is available
  // (NIGHT_MODE_READY, or the developer preview unlock), so a saved
  // 'night' can never put a half-converted page in the dark.
  const [mode, setModeState] = useState(() => modeStore.read());
  const [nightAvailable] = useState(() => nightModeAvailable());
  const effectiveMode = nightAvailable ? mode : DEFAULT_MODE;
  const [containerPosition_, setcontainerPosition_] = useState({ value: 'wide', label: 'Wide' });
  const body = document.querySelector('body');
  const [windowWidth, setWindowWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);

  // layout
  const layoutOption = [
    { value: 'vertical', label: 'Vertical' },
    { value: 'horizontal', label: 'Horizontal' },
  ];
  const sideBarOption = [
    { value: 'compact', label: 'Compact' },
    { value: 'full', label: 'Full' },
    { value: 'mini', label: 'Mini' },
    { value: 'modern', label: 'Modern' },
    { value: 'overlay', label: 'Overlay' },
    { value: 'icon-hover', label: 'Icon-hover' },
  ];
  // Kept as a single-entry list so anything still reading it keeps
  // working; the three-skin picker it used to feed is gone.
  const backgroundOption = [{ value: 'light', label: 'Light' }];
  const sidebarpositions = [
    { value: 'fixed', label: 'Fixed' },
    { value: 'static', label: 'Static' },
  ];
  const headerPositions = [
    { value: 'fixed', label: 'Fixed' },
    { value: 'static', label: 'Static' },
  ];
  const containerPosition = [
    { value: 'wide-boxed', label: 'Wide Boxed' },
    { value: 'boxed', label: 'Boxed' },
    { value: 'wide', label: 'Wide' },
  ];
  const colors = [
    'color_1',
    'color_2',
    'color_3',
    'color_4',
    'color_5',
    'color_6',
    'color_7',
    'color_8',
    'color_9',
    'color_10',
    'color_11',
    'color_12',
    'color_13',
    'color_14',
    'color_15',
  ];
  const directionPosition = [
    { value: 'ltr', label: 'LTR' },
    { value: 'rtl', label: 'RTL' },
  ];
  const fontFamily = [
    { value: 'roboto', label: 'Roboto' },
    { value: 'poppins', label: 'Poppins' },
    { value: 'cairo', label: 'Cairo' },
    { value: 'opensans', label: 'Open Sans' },
    { value: 'HelveticaNeue', label: 'HelveticaNeue' },
  ];
  const changePrimaryColor = useCallback((name) => {
    setPrimaryColor(name);
    body.setAttribute('data-primary', name);
  }, [body]);

  const changeNavigationHader = useCallback((name) => {
    setNavigationHader(name);
    body.setAttribute('data-nav-headerbg', name);
  }, [body]);

  const chnageHaderColor = useCallback((name) => {
    setHaderColor(name);
    body.setAttribute('data-headerbg', name);
  }, [body]);

  const chnageSidebarColor = useCallback((name) => {
    setSidebarColor(name);
    body.setAttribute('data-sidebarbg', name);
  }, [body]);

  const changeSideBarPostion = useCallback((name) => {
    setSidebarposition(name);
    body.setAttribute('data-sidebar-position', name.value);
  }, [body]);

  const changeDirectionLayout = useCallback((name) => {
    setDirection(name);
    body.setAttribute('direction', name.value);
    let html = document.querySelector('html');
    html.setAttribute('dir', name.value);
    html.className = name.value;
  }, [body]);

  const changeSideBarLayout = useCallback((name) => {
    if (name.value === 'horizontal') {
      if (sideBarStyle.value === 'overlay') {
        setSidebarLayout(name);
        body.setAttribute('data-layout', name.value);
        setSideBarStyle({ value: 'full', label: 'Full' });
        body.setAttribute('data-sidebar-style', 'full');
      } else {
        setSidebarLayout(name);
        body.setAttribute('data-layout', name.value);
      }
    } else {
      setSidebarLayout(name);
      body.setAttribute('data-layout', name.value);
    }
  }, [body, sideBarStyle.value]);

  const changeSideBarStyle = useCallback((name) => {
    if (sidebarLayout.value === 'horizontal') {
      if (name.value === 'overlay') {
        Swal.fire({
          icon: 'error',
          title: 'Layout Error',
          text: 'Sorry! Overlay is not possible in Horizontal layout.',
        });
      } else {
        setSideBarStyle(name);
        setIconHover(name.value === 'icon-hover' ? '_i-hover' : '');
        body.setAttribute('data-sidebar-style', name.value);
      }
    } else {
      setSideBarStyle(name);
      setIconHover(name.value === 'icon-hover' ? '_i-hover' : '');
      body.setAttribute('data-sidebar-style', name.value);
    }
  }, [body, sidebarLayout.value]);

  const ChangeIconSidebar = useCallback((value) => {
    if (sideBarStyle.value === 'icon-hover') {
      if (value) {
        setSidebariconHover(true);
      } else {
        setSidebariconHover(false);
      }
    }
  }, [sideBarStyle.value]);

  const changeHeaderPostion = useCallback((name) => {
    setHeaderposition(name);
    body.setAttribute('data-header-position', name.value);
  }, [body]);

  const openMenuToggle = useCallback(() => {
    sideBarStyle.value === 'overlay' ? setMenuToggle(true) : setMenuToggle(false);
  }, [sideBarStyle.value]);

  // Kept so existing callers do not break. There is one skin now, so
  // this only ever reasserts it.
  const changeBackground = useCallback(() => {
    body.setAttribute('data-theme-version', 'light');
  }, [body]);

  // ── palette ───────────────────────────────────────────────────
  // One code path for both kinds of choice: a selection is resolved
  // to thirteen values and those are painted onto :root. A prebuilt
  // palette hands over a finished map, a custom one is derived from
  // its accent. Nothing else in the app needs to know which it was.

  const changePalette = useCallback((selection) => {
    setPalette(selection);
    paletteStore.write(selection);
  }, []);

  const selectPalette = useCallback(
    (id) => changePalette({ id }),
    [changePalette],
  );

  const setCustomPalette = useCallback(
    (accent, tint = 100) => changePalette({ id: 'custom', accent, tint }),
    [changePalette],
  );

  const resetPalette = useCallback(() => {
    paletteStore.clear();
    setPalette({ id: DEFAULT_PALETTE_ID });
  }, []);

  // Paint on mount and on every change. Deliberately not debounced:
  // the custom picker drags a colour input, and setProperty on a
  // dozen values is cheap enough that the preview should track the
  // pointer rather than catch up after it.
  useEffect(() => {
    applyMode(effectiveMode);
    applyTokens(resolveTokens(palette, effectiveMode), resolveShell(palette));
  }, [palette, effectiveMode]);

  const setMode = useCallback((next) => {
    const value = next === 'night' ? 'night' : 'day';
    setModeState(value);
    modeStore.write(value);
  }, []);

  const changeContainerPosition = useCallback((name) => {
    setcontainerPosition_(name);
    body.setAttribute('data-container', name.value);
    name.value === 'boxed' && changeSideBarStyle({ value: 'overlay', label: 'Overlay' });
  }, [body, changeSideBarStyle]);

  useEffect(() => {
    const isNarrowScreen = window.matchMedia('(max-width: 767px)').matches;
    if (isNarrowScreen) {
      body.setAttribute('data-sidebar-style', 'overlay');
    }
  }, [body]);
  // else{
  //   body.setAttribute("data-sidebar-style", "mini")
  // }

  // Set initial body attributes, including persisted theme
  useEffect(() => {
    const body = document.querySelector('body');
    body.setAttribute('data-typography', 'roboto');
    // Apply persisted or default theme
    body.setAttribute('data-theme-version', background.value);
    body.setAttribute('data-layout', 'vertical');
    body.setAttribute('data-primary', 'color_1');
    body.setAttribute('data-nav-headerbg', 'color_3');
    body.setAttribute('data-headerbg', 'color_1');
    body.setAttribute('data-sidebar-style', 'mini');
    body.setAttribute('data-sidebarbg', 'color_1');
    body.setAttribute('data-primary', 'color_1');
    body.setAttribute('data-sidebar-position', 'fixed');
    body.setAttribute('data-header-position', 'fixed');
    body.setAttribute('data-container', 'wide');
    body.setAttribute('direction', 'ltr');
    let resizeWindow = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);

      if (window.innerWidth >= 768 && window.innerWidth < 1200) {
        body.setAttribute('data-sidebar-style', 'mini');
      } else if (window.innerWidth <= 768) {
        body.setAttribute('data-sidebar-style', 'overlay');
      } else {
        body.setAttribute('data-sidebar-style', 'full');
      }
    };
    resizeWindow();
    window.addEventListener('resize', resizeWindow);
    return () => window.removeEventListener('resize', resizeWindow);
  }, [background]);

  const contextValue = useMemo(() => ({
    body,
    sideBarOption,
    layoutOption,
    backgroundOption,
    sidebarposition,
    headerPositions,
    containerPosition,
    directionPosition,
    fontFamily,
    primaryColor,
    navigationHader,
    windowWidth,
    windowHeight,
    changePrimaryColor,
    changeNavigationHader,
    changeSideBarStyle,
    sideBarStyle,
    changeSideBarPostion,
    sidebarpositions,
    changeHeaderPostion,
    headerposition,
    changeSideBarLayout,
    sidebarLayout,
    changeDirectionLayout,
    changeContainerPosition,
    direction,
    colors,
    haderColor,
    chnageHaderColor,
    chnageSidebarColor,
    sidebarColor,
    iconHover,
    ChangeIconSidebar,
    sidebariconHover,
    menuToggle,
    openMenuToggle,
    changeBackground,
    background,
    containerPosition_,
    // palette
    palette,
    palettes: PALETTES,
    selectPalette,
    setCustomPalette,
    resetPalette,
    // mode
    mode: effectiveMode,
    setMode,
    nightModeAvailable: nightAvailable,
  }), [
    body,
    sidebarposition,
    primaryColor,
    navigationHader,
    windowWidth,
    windowHeight,
    changePrimaryColor,
    changeNavigationHader,
    changeSideBarStyle,
    sideBarStyle,
    changeSideBarPostion,
    changeHeaderPostion,
    headerposition,
    changeSideBarLayout,
    sidebarLayout,
    changeDirectionLayout,
    changeContainerPosition,
    direction,
    haderColor,
    chnageHaderColor,
    chnageSidebarColor,
    sidebarColor,
    iconHover,
    ChangeIconSidebar,
    sidebariconHover,
    menuToggle,
    openMenuToggle,
    changeBackground,
    background,
    containerPosition_,
    palette,
    selectPalette,
    setCustomPalette,
    resetPalette,
    effectiveMode,
    setMode,
    nightAvailable,
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {props.children}
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
